import React, { useMemo, useState, useEffect } from "react";
import { MAJOR_ARCANA } from "./data/majorArcana";
import { getTodayCardIndex, getLocalDateKey, checkAndIncrementStreak, getDailySpread } from "./lib/dailyDraw";
import { CardTabs } from "./components/CardTabs";
import { ArcanaLibrary } from "./components/ArcanaLibrary";
export type DmTarotTheme = "auto" | "dark" | "light";

export type DmTarotConfig = {
  theme?: DmTarotTheme;
  newsletterTitle?: string;
  newsletterSub?: string;
  newsletterEmbedHtml?: string;
};

type AppProps = {
  config: DmTarotConfig;
};

export default function App({ config }: AppProps) {
  const todayKey = useMemo(() => getLocalDateKey(), []);
  const todayIndex = useMemo(() => getTodayCardIndex(MAJOR_ARCANA.length), []);
  const spreadIndices = useMemo(() => getDailySpread(MAJOR_ARCANA.length), []);
  
  const dailyCard = MAJOR_ARCANA[todayIndex];
  const spreadCards = spreadIndices.map(i => MAJOR_ARCANA[i]);

  const [mode, setMode] = useState<"daily" | "spread">("daily");
  const [revealed, setRevealed] = useState(false);
  const [spreadRevealed, setSpreadRevealed] = useState<boolean[]>([false, false, false]);
  const [streak, setStreak] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const s = checkAndIncrementStreak(todayKey);
    setStreak(s);
    
    // Show confetti for milestone streaks
    if (s === 7 || s === 30 || s === 100 || (s > 0 && s % 100 === 0)) {
      setShowConfetti(true);
      createConfetti();
      setTimeout(() => setShowConfetti(false), 4000);
    }
  }, [todayKey]);

  // Move server-rendered newsletter form into React app
  useEffect(() => {
    if (config.newsletterEmbedHtml === 'SERVER_RENDERED') {
      const serverForm = document.getElementById('dm-newsletter-server-rendered');
      const targetDaily = document.getElementById('dm-newsletter-target-daily');
      const targetSpread = document.getElementById('dm-newsletter-target-spread');
      
      if (serverForm && targetDaily && targetSpread) {
        // Clone the form for both sections
        const formContentDaily = serverForm.cloneNode(true) as HTMLElement;
        const formContentSpread = serverForm.cloneNode(true) as HTMLElement;
        
        formContentDaily.style.display = 'block';
        formContentSpread.style.display = 'block';
        formContentDaily.id = '';
        formContentSpread.id = '';
        
        targetDaily.appendChild(formContentDaily);
        targetSpread.appendChild(formContentSpread);
        
        // Hide the original
        serverForm.style.display = 'none';
      }
    }
  }, [config.newsletterEmbedHtml]);

  const createConfetti = () => {
    const colors = ['#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#3b82f6'];
    const confettiCount = 50;
    
    for (let i = 0; i < confettiCount; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = Math.random() * 0.5 + 's';
        confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
        document.body.appendChild(confetti);
        
        setTimeout(() => confetti.remove(), 5000);
      }, i * 30);
    }
  };

  const newsletterTitle =
    config.newsletterTitle ?? "Want the deeper layer for today's card?";
  const newsletterSub =
    config.newsletterSub ??
    "Subscribe to unlock richer interpretations and save your journey.";

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch(e.key) {
        case ' ':
          e.preventDefault();
          if (mode === 'daily' && !revealed) {
            setRevealed(true);
          }
          break;
        case '1':
          setMode('daily');
          break;
        case '2':
          setMode('spread');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [mode, revealed]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-joy-50 via-joy-100 to-sky-100 font-sans text-slate-800">
      <div className="w-full px-8 py-6">
        {/* Page Header */}
        <div className="w-full flex flex-col items-center justify-center mb-6 bg-white/70 backdrop-blur-md rounded-2xl px-6 py-4 shadow-sm border border-white">
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-4xl font-serif font-bold bg-clip-text text-transparent bg-gradient-to-r from-joy-600 via-purple-600 to-sky-600">
              DivineMarkings Tarot
            </h1>
            {streak > 1 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                <span>🔥</span>
                <span>{streak} Day Streak</span>
              </div>
            )}
          </div>
          <div className="text-sm text-slate-500 font-medium">{todayKey}</div>
          
          {/* Keyboard shortcuts hint */}
          <div className="mt-3 text-xs text-slate-400 flex gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-0.5 bg-slate-100 rounded border border-slate-300 font-mono">Space</kbd>
              <span>Reveal</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-0.5 bg-slate-100 rounded border border-slate-300 font-mono">1</kbd>
              <span>Daily</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-0.5 bg-slate-100 rounded border border-slate-300 font-mono">2</kbd>
              <span>Spread</span>
            </span>
          </div>
        </div>

        {/* Dashboard Grid: Responsive 3-column layout */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT SIDEBAR - Mode Selector & Stats */}
          <div className="lg:col-span-3 space-y-6">
            {/* Mode Selector Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white">
              <h2 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-joy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Choose Your Reading
              </h2>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Select a single card for daily guidance or a 3-card spread for deeper insight into your journey.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => setMode("daily")}
                  className={`w-full text-left px-4 py-4 rounded-xl transition-all font-medium ${
                    mode === "daily"
                      ? "bg-gradient-to-r from-joy-400 to-joy-600 text-white shadow-lg scale-[1.02]"
                      : "bg-joy-50 text-slate-700 hover:bg-joy-100"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-base">Daily Card</div>
                      <div className={`text-xs mt-1 ${mode === "daily" ? "text-white/80" : "text-slate-500"}`}>
                        Single card guidance
                      </div>
                    </div>
                    {mode === "daily" && (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={() => setMode("spread")}
                  className={`w-full text-left px-4 py-4 rounded-xl transition-all font-medium ${
                    mode === "spread"
                      ? "bg-gradient-to-r from-purple-400 to-purple-600 text-white shadow-lg scale-[1.02]"
                      : "bg-joy-50 text-slate-700 hover:bg-joy-100"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-base">3-Card Spread</div>
                      <div className={`text-xs mt-1 ${mode === "spread" ? "text-white/80" : "text-slate-500"}`}>
                        Past • Present • Future
                      </div>
                    </div>
                    {mode === "spread" && (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-gradient-to-br from-white/80 to-joy-50/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-joy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                Your Journey
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Current Streak</span>
                  <span className="text-lg font-bold text-joy-700">{streak} days</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Mode</span>
                  <span className="text-lg font-bold text-purple-600">{mode === "daily" ? "Daily" : "Spread"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* MAIN CONTENT AREA - Card Display */}
          <div className="lg:col-span-6">
            {mode === "daily" ? (
              <div className="space-y-6">
                {/* Daily Card Section */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white min-h-[500px]">
                  {!revealed ? (
                    <div className="flex flex-col items-center justify-center h-full animate-fade-in">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-serif font-bold text-slate-800 mb-3">
                          Today's Card Awaits
                        </h2>
                        <p className="text-lg text-slate-600">
                          Discover the guidance meant for you today
                        </p>
                      </div>
                      
                      <div className="w-64 h-96 bg-gradient-to-br from-joy-400 via-joy-500 to-purple-600 rounded-2xl shadow-2xl mb-8 flex items-center justify-center transform hover:scale-105 hover:shadow-3xl transition-all duration-300 cursor-pointer" onClick={() => setRevealed(true)}>
                        <div className="text-white text-6xl font-serif">?</div>
                      </div>
                      
                      <button
                        className="group relative px-10 py-5 text-xl font-bold text-white transition-all duration-300 bg-gradient-to-r from-joy-500 via-joy-600 to-purple-600 rounded-2xl shadow-2xl hover:shadow-purple-500/50 hover:-translate-y-2 hover:scale-105 active:translate-y-0 active:scale-100"
                        onClick={() => setRevealed(true)}
                        type="button"
                      >
                        <span className="relative z-10">✨ Reveal Your Card</span>
                      </button>
                    </div>
                  ) : (
                    <div className="animate-fade-in-scale">
                      {/* Card Image */}
                      <div className="flex justify-center mb-6">
                        <img 
                          src={dailyCard.imageUrl} 
                          alt={dailyCard.name}
                          className="animate-flip-card w-full max-w-xs md:max-w-sm lg:w-80 h-auto rounded-2xl shadow-2xl border-4 border-white hover:shadow-3xl hover:-translate-y-2 transition-all duration-300"
                        />
                      </div>

                      {/* Card Header */}
                      <div className="text-center mb-6">
                        <h2 className="text-4xl font-serif font-bold text-slate-800 mb-2">
                          {dailyCard.name}
                        </h2>
                        <div className="flex flex-wrap justify-center gap-2 mt-3">
                          {dailyCard.keywords.map((kw) => (
                            <span key={kw} className="px-3 py-1 bg-joy-100 text-joy-700 text-xs font-semibold rounded-full">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Card Tabs (4 tabs now - removed Learn) */}
                      <CardTabs card={dailyCard} />

                      {/* Always-Visible Learn More Section */}
                      <div className="mt-8 p-6 bg-gradient-to-r from-sky-500 to-purple-600 rounded-2xl shadow-lg">
                        <div className="flex items-center justify-between">
                          <div className="text-white">
                            <h3 className="text-lg font-bold mb-1">Want to Learn More?</h3>
                            <p className="text-sm text-white/90">Dive deeper into the symbolism and meaning of {dailyCard.name}</p>
                          </div>
                          <a
                            href={dailyCard.articleUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-6 py-3 bg-white text-purple-700 font-bold rounded-xl hover:bg-joy-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 whitespace-nowrap"
                          >
                            Read Full Guide →
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Newsletter Section (Daily mode only) */}
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white">
                  <div className="text-center">
                    <h3 className="text-2xl font-serif font-bold text-joy-800 mb-2">{newsletterTitle}</h3>
                    <p className="text-slate-600 mb-6">{newsletterSub}</p>
                    {config.newsletterEmbedHtml ? (
                      <div id="dm-newsletter-target-daily" />
                    ) : (
                      <div className="text-sm text-slate-400 italic border border-dashed border-slate-300 rounded-xl p-6 bg-slate-50/50">
                        Newsletter signup form will appear here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* 3-Card Spread Mode */
              <div className="space-y-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white">
                  <h2 className="text-3xl font-serif font-bold text-center text-slate-800 mb-6">
                    Past • Present • Future
                  </h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {spreadCards.map((c, i) => (
                      <div key={`${c.id}-${i}`} className="flex flex-col">
                        <div className="text-center mb-3">
                          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider rounded-full">
                            {i === 0 ? "Past" : i === 1 ? "Present" : "Future"}
                          </span>
                        </div>
                        
                        {!spreadRevealed[i] ? (
                          <button
                            onClick={() => {
                              const newRevealed = [...spreadRevealed];
                              newRevealed[i] = true;
                              setSpreadRevealed(newRevealed);
                            }}
                            className="aspect-[2/3] bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl shadow-xl flex items-center justify-center text-white font-serif text-lg hover:-translate-y-2 hover:shadow-2xl hover:scale-105 transition-all duration-300 group"
                          >
                            <span>Tap to Reveal</span>
                          </button>
                        ) : (
                          <div className="animate-fade-in-scale space-y-3">
                            <img 
                              src={c.imageUrl} 
                              alt={c.name}
                              className="animate-flip-card w-full aspect-[2/3] object-cover rounded-2xl shadow-lg border-2 border-purple-200 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                            />
                            <div className="bg-white rounded-xl p-4 shadow-md border border-purple-100">
                              <h3 className="font-serif font-bold text-lg text-slate-800 mb-2">{c.name}</h3>
                              <p className="text-sm text-slate-600 leading-relaxed mb-3">{c.shortMeaning}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {c.keywords.slice(0, 3).map((kw) => (
                                  <span key={kw} className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs font-medium rounded">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <a 
                              href={c.articleUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-center px-4 py-2 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 text-sm"
                            >
                              Learn More →
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Newsletter for Spread Mode */}
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white">
                  <div className="text-center">
                    <h3 className="text-2xl font-serif font-bold text-joy-800 mb-2">{newsletterTitle}</h3>
                    <p className="text-slate-600 mb-6">{newsletterSub}</p>
                    {config.newsletterEmbedHtml ? (
                      <div id="dm-newsletter-target-spread" />
                    ) : (
                      <div className="text-sm text-slate-400 italic border border-dashed border-slate-300 rounded-xl p-6 bg-slate-50/50">
                        Newsletter signup form will appear here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR - Arcana Library */}
          <div className="lg:col-span-3">
            <ArcanaLibrary cards={MAJOR_ARCANA} />
          </div>

        </div>
      </div>
    </div>
  );
}
